import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { ErrorNotice, PageHead } from '../components/ui';
import { useI18n } from '../lib/i18n';
import { todayInRome } from '../lib/memberStatus';
import { useSites } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { ShiftPlanner } from '../components/ShiftPlanner';

export interface CalendarAbsence {
  id: string;
  member_id: string;
  start_date: string;
  end_date: string;
  status: 'pending' | 'approved';
  quantity: number;
  unit: 'days' | 'hours';
  category: 'leave' | 'private' | 'remote';
  type_name: string | null;
}
export interface TeamCalendar { people: Array<{ id: string; name: string; site_id: string | null }>; absences: CalendarAbsence[] }

/** Every day of the month as YYYY-MM-DD, computed in UTC so no time zone shifts a date. */
export function monthDays(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const isWeekend = (day: string) => [0, 6].includes(new Date(`${day}T12:00:00Z`).getUTCDay());

/** People away (approved, not remote) per day: the coverage row. */
export function absentPerDay(calendar: TeamCalendar, days: string[]): number[] {
  return days.map((day) => new Set(calendar.absences
    .filter((a) => a.status === 'approved' && a.category !== 'remote' && a.start_date <= day && a.end_date >= day)
    .map((a) => a.member_id)).size);
}

export function TeamCalendarPage() {
  const { t, locale } = useI18n();
  const { active, isHr } = useAuth();
  const companyId = active?.company_id;
  const sites = useSites();
  const [month, setMonth] = useState(todayInRome().slice(0, 7));
  const [site, setSite] = useState('');
  const days = useMemo(() => monthDays(month), [month]);
  const query = useQuery({
    queryKey: ['teamCalendar', companyId, month, site],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('team_absence_calendar', {
        p_company_id: companyId, p_from: days[0], p_to: days[days.length - 1], p_site_id: site || null,
      });
      if (error) throw error;
      return data as TeamCalendar;
    },
  });
  const calendar = query.data;
  const coverage = calendar ? absentPerDay(calendar, days) : [];
  const title = new Date(`${month}-15T12:00:00Z`).toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const label = (a: CalendarAbsence) => [
    a.category === 'private' ? t('calendar.private') : a.type_name ?? '',
    a.unit === 'hours' ? `${Number(a.quantity)} h` : '',
    a.status === 'pending' ? t('calendar.pending') : '',
  ].filter(Boolean).join(' · ');

  return (
    <>
      <PageHead title={t('calendar.title')} />
      <section className="card stack">
        <p className="small muted">{t(isHr ? 'calendar.explainHr' : 'calendar.explainManager')}</p>
        <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <button className="btn" onClick={() => setMonth(shiftMonth(month, -1))} aria-label={t('calendar.previous')}>‹</button>
          <strong style={{ minWidth: 160, textAlign: 'center' }}>{title}</strong>
          <button className="btn" onClick={() => setMonth(shiftMonth(month, 1))} aria-label={t('calendar.next')}>›</button>
          {isHr ? (
            <label className="field">{t('calendar.site')}<select value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="">{t('calendar.allSites')}</option>
              {(sites.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></label>
          ) : null}
        </div>
        <div className="row small" style={{ flexWrap: 'wrap', gap: 12 }}>
          <span><span className="cal-swatch cal-leave" /> {t('calendar.legendLeave')}</span>
          <span><span className="cal-swatch cal-pending" /> {t('calendar.legendPending')}</span>
          <span><span className="cal-swatch cal-remote" /> {t('calendar.legendRemote')}</span>
        </div>
        <ErrorNotice error={query.error} />
        {query.isPending ? <p>{t('common.loading')}</p> : calendar && calendar.people.length === 0 ? <p className="empty">{t('calendar.empty')}</p> : calendar ? (
          <div className="table-wrap">
            <table className="cal-grid">
              <thead>
                <tr>
                  <th scope="col">{t('calendar.person')}</th>
                  {days.map((d) => <th key={d} scope="col" className={isWeekend(d) ? 'cal-weekend' : ''}>{Number(d.slice(8))}</th>)}
                </tr>
              </thead>
              <tbody>
                {calendar.people.map((p) => (
                  <tr key={p.id}>
                    <th scope="row">{p.name}</th>
                    {days.map((d) => {
                      const matches = calendar.absences.filter((x) => x.member_id === p.id && x.start_date <= d && x.end_date >= d);
                      const a = matches.find((x) => x.category !== 'remote' && x.status === 'approved') ?? matches.find((x) => x.category !== 'remote') ?? matches[0];
                      const titles = matches.map(label).join(' + ');
                      const cls = a ? (a.category === 'remote' ? 'cal-remote' : a.status === 'pending' ? 'cal-pending' : 'cal-leave') : '';
                      return <td key={d} className={`${cls} ${isWeekend(d) ? 'cal-weekend' : ''}`} title={matches.length ? titles : undefined} aria-label={matches.length ? `${p.name} ${d}: ${titles}` : undefined} />;
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">{t('calendar.absent')}</th>
                  {coverage.map((n, i) => <td key={days[i]} className={isWeekend(days[i]) ? 'cal-weekend' : ''}>{n || ''}</td>)}
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null}
      </section>
      {companyId?<ShiftPlanner companyId={companyId} month={month} siteId={site} sites={sites.data??[]}/>:null}
    </>
  );
}
